import { useFormik } from "formik";
import * as Yup from "yup";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { updateConfig } from "../../../../../store/slices/User/SubscriptionPlans/corporateOfferSlice";
import {
  useGetCorporateOfferDetailsQuery,
  useGetCorporateOfferPreviewMutation,
  useGetSubscriptionPlanListDataQuery,
} from "../../../../../store/queries/Subscription";
import { createAndUpdateCorporateOffer } from "../api";

const useAddCorporateOffer = (handleCreateClick, refetch) => {
  const dispatch = useDispatch();
  const [isOfferSaving, setIsOfferSaving] = useState(false);

  const { isEdit, editOfferData, pricing, isPricingLoading } = useSelector(
    (state) => state.corporateOffer
  );

  const [getOfferPreview] = useGetCorporateOfferPreviewMutation();

  /**
   * The row the table hands over carries only what the table shows, and the form asks for
   * more than that - the plan id behind the plan name, the eligibility rules, the per
   * customer limit. Those live on the detail endpoint, so editing fetches it and fills the
   * form from the two merged, the row first so the fields already on screen do not blank
   * out while the request is in flight.
   *
   * Without this a draft edit posts back empty eligibility and silently drops a mobile list
   * the offer was restricted to, because editing a draft rebuilds it from what is posted.
   */
  const { data: offerDetails } = useGetCorporateOfferDetailsQuery(
    { offer_id: editOfferData?.offer_id },
    { skip: !isEdit || !editOfferData?.offer_id }
  );

  const offerData = useMemo(
    () => ({ ...(editOfferData ?? {}), ...(offerDetails?.data ?? {}) }),
    [editOfferData, offerDetails]
  );

  /**
   * Published corporate plans, and nothing else. An offer freezes a snapshot of the plan at
   * creation, and the cart service refuses anything else with subscription_plan_not_available
   * - so offering drafts, or the public plans the app sells at their own price, would only
   * produce a refusal after the team had filled the form in.
   *
   * page_size, not per_page. The cart service does read per_page, but nothing here talks to
   * it directly: the base query in store/queries/Subscription builds the URL from a fixed
   * list of keys and is the one place the rename happens, sending page_size on as per_page.
   * A literal per_page is not on that list, so it never reaches the wire at all - which left
   * this picker on the service's default 10 and quietly hid the eleventh plan onwards.
   */
  const { data: planList = {} } = useGetSubscriptionPlanListDataQuery({
    status: 2,
    audience: 2,
    page: 1,
    page_size: 200,
  });

  const plans = useMemo(() => planList?.result ?? [], [planList]);

  /**
   * A draft is freely editable. Once an offer is active the organisation has been quoted on
   * it, so only more seats and a later window end are accepted - everything else is refused
   * by the service with corporate_offer_locked, and is disabled here so the team is not
   * invited to type into a field that cannot be saved.
   */
  const isLocked = isEdit && editOfferData?.status_label !== "draft";

  const emptyToNull = (value, original) => (original === "" ? null : value);

  const validation = Yup.object({
    organisation: Yup.string().trim().required("Enter the organisation").max(150),
    plan_id: Yup.string().required("Select a plan"),
    offer_type: Yup.string().required("Select an offer type"),
    code_type: Yup.string().required("Select a code type"),
    offer_code: Yup.string()
      .trim()
      .required("Enter a code")
      .min(4, "At least 4 characters")
      .max(40, "At most 40 characters")
      .matches(
        /^[A-Za-z0-9-]+$/,
        "Letters, numbers and hyphens only"
      ),
    seats: Yup.number()
      .transform(emptyToNull)
      .required("Enter the number of seats")
      .integer("Enter a whole number")
      .min(1, "At least 1 seat")
      .max(100000, "At most 100000 seats")
      // An active offer may grow but never shrink below what employees have already taken.
      .test(
        "not-below-used",
        "Seats cannot go below the seats already used",
        (value) =>
          !isEdit ||
          value === undefined ||
          value === null ||
          value >= (editOfferData?.used_seats ?? 0)
      ),
    discount_percent: Yup.number()
      .transform(emptyToNull)
      .required("Enter a discount")
      .min(0, "Cannot be negative")
      .lessThan(100, "Must be under 100%"),
    code_valid_from: Yup.string().required("Choose a start date"),
    code_valid_to: Yup.string()
      .required("Choose an end date")
      .test(
        "after-start",
        "The end date cannot be before the start date",
        function (value) {
          const { code_valid_from } = this.parent;
          if (!value || !code_valid_from) {
            return true;
          }
          return value >= code_valid_from;
        }
      ),
    plan_validity_days: Yup.number()
      .transform(emptyToNull)
      .nullable()
      .integer("Enter a whole number")
      .min(1, "At least 1 day")
      .max(730, "At most 730 days"),
    email_domain: Yup.string()
      .nullable()
      .when("eligibility_type", {
        is: "email_domain",
        then: (schema) => schema.required("Enter an email domain").max(100),
      }),
    mobile_numbers: Yup.array().when("eligibility_type", {
      is: "mobile_list",
      then: (schema) => schema.min(1, "Add at least one mobile number"),
    }),
    limit_per_customer: Yup.number()
      .transform(emptyToNull)
      .nullable()
      .integer("Enter a whole number")
      .min(1, "At least 1")
      .max(100, "At most 100"),
  });

  const formik = useFormik({
    initialValues: {
      organisation: "",
      plan_id: "",
      offer_type: "prepaid",
      code_type: "shared",
      offer_code: "",
      seats: "",
      discount_percent: "",
      code_valid_from: "",
      code_valid_to: "",
      plan_validity_days: "",
      eligibility_type: "open",
      mobile_numbers: [],
      email_domain: "",
      limit_per_customer: 1,
    },
    validationSchema: validation,
    onSubmit: async (values) => {
      setIsOfferSaving(true);

      // An update to a live offer sends only what a live offer may change. Sending the rest
      // unchanged would still be refused, and would read as though the team had tried to
      // alter terms the organisation was quoted on.
      const data = isLocked
        ? {
            offer_id: editOfferData?.offer_id,
            seats: Number(values?.seats),
            code_valid_to: values?.code_valid_to,
          }
        : {
            organisation: values?.organisation,
            plan_id: values?.plan_id,
            offer_type: values?.offer_type,
            code_type: values?.code_type,
            offer_code: values?.offer_code,
            seats: Number(values?.seats),
            discount_percent: Number(values?.discount_percent),
            code_valid_from: values?.code_valid_from,
            code_valid_to: values?.code_valid_to,
            plan_validity_days:
              values?.plan_validity_days === "" ||
              values?.plan_validity_days === null
                ? null
                : Number(values?.plan_validity_days),
            eligibility_type: values?.eligibility_type,
            mobile_numbers:
              values?.eligibility_type === "mobile_list"
                ? values?.mobile_numbers
                : null,
            email_domain:
              values?.eligibility_type === "email_domain"
                ? values?.email_domain
                : null,
            limit_per_customer:
              values?.limit_per_customer === "" ||
              values?.limit_per_customer === null
                ? null
                : Number(values?.limit_per_customer),
            ...(isEdit ? { offer_id: editOfferData?.offer_id } : {}),
          };

      const response = await createAndUpdateCorporateOffer(data);

      if (response?.data?.status_code === 200) {
        toast.success(isEdit ? `Offer updated` : `Offer created as a draft`);
        setIsOfferSaving(false);
        handleCreateClick();
        refetch();
        return;
      }

      const errors = response?.errors ?? response?.data?.errors;
      if (errors) {
        Object.keys(errors).forEach((field) => {
          formik.setFieldError(
            field,
            Array.isArray(errors[field]) ? errors[field][0] : errors[field]
          );
        });
      } else {
        toast.error(
          response?.message ?? response?.data?.message ?? `Something went wrong!`
        );
      }
      setIsOfferSaving(false);
    },
  });

  useEffect(() => {
    if (isEdit && offerData && Object.keys(offerData).length > 0) {
      formik?.setValues({
        organisation: offerData?.organisation ?? "",
        plan_id: offerData?.plan_id ?? "",
        offer_type: offerData?.offer_type ?? "prepaid",
        code_type: offerData?.code_type ?? "shared",
        offer_code: offerData?.offer_code ?? "",
        seats: offerData?.seats ?? "",
        discount_percent: offerData?.discount_percent ?? "",
        code_valid_from: String(offerData?.code_valid_from ?? "").slice(0, 10),
        code_valid_to: String(offerData?.code_valid_to ?? "").slice(0, 10),
        plan_validity_days: offerData?.plan_validity_days ?? "",
        eligibility_type: offerData?.eligibility_type ?? "open",
        mobile_numbers: offerData?.eligible_mobiles ?? [],
        email_domain: offerData?.email_domain ?? "",
        limit_per_customer: offerData?.limit_per_customer ?? 1,
      });
    }
    //eslint-disable-next-line
  }, [isEdit, offerData]);

  /**
   * The pricing panel, live as the team types. max_liability is the figure finance signs off
   * before the offer goes live, so it is worth showing before anything is saved.
   */
  useEffect(() => {
    const { plan_id, offer_type, seats, discount_percent } = formik.values;

    if (!plan_id || discount_percent === "" || discount_percent === null) {
      return;
    }

    const timer = setTimeout(async () => {
      dispatch(
        updateConfig((state) => {
          state.isPricingLoading = true;
        })
      );

      const response = await getOfferPreview({
        plan_id,
        offer_type,
        seats: Number(seats || 0),
        discount_percent: Number(discount_percent),
      });

      dispatch(
        updateConfig((state) => {
          state.isPricingLoading = false;
          // A refusal (an unpublished plan, a discount that rounds an item to zero) comes
          // back as a 422 rather than a payload, and is surfaced on the panel.
          state.pricing = response?.data?.data ?? {
            error:
              response?.error?.data?.message ?? "Unable to price this offer",
          };
        })
      );
    }, 500);

    return () => clearTimeout(timer);
    //eslint-disable-next-line
  }, [
    formik.values.plan_id,
    formik.values.offer_type,
    formik.values.seats,
    formik.values.discount_percent,
  ]);

  /**
   * The API takes the numbers themselves, so a CSV handed over by the organisation is parsed
   * here rather than uploaded.
   */
  const handleMobileCsvChange = (event) => {
    const file = event?.target?.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const numbers = String(reader.result)
        .split(/[\r\n,;]+/)
        .map((value) => value.trim())
        .filter((value) => value.length >= 6 && value.length <= 20);

      formik.setFieldValue("mobile_numbers", Array.from(new Set(numbers)));
    };
    reader.readAsText(file);
  };

  /**
   * The template forwarded to the organisation, so the list comes back in a shape the parser
   * above accepts. Deliberately headerless and single column: nothing skips the first row, and
   * a "mobile" header clears the six character floor, so it would load as a number. A second
   * column would do the same - an employee id like EMP-00123 normalises to digits and is
   * stored as an eligible mobile that can never match an account.
   */
  const handleSampleCsvDownload = () => {
    const csv = [
      "51234567",
      "60123456",
      "+965 6012 3456",
      "0096550998877",
      "96599887766",
    ].join("\n");

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `corporate-offer-mobile-numbers-sample.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleMobileTextChange = (event) => {
    const numbers = String(event?.target?.value ?? "")
      .split(/[\r\n,;]+/)
      .map((value) => value.trim())
      .filter(Boolean);

    formik.setFieldValue("mobile_numbers", numbers);
  };

  const getFieldError = (fieldName) => {
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    }
    return "";
  };

  const closeModal = () => {
    dispatch(
      updateConfig((state) => {
        state.showCreateModal = false;
        state.isEdit = false;
        state.pricing = {};
      })
    );
  };

  const planObj = useMemo(
    () => plans?.filter((item) => item?.id === formik.values.plan_id),
    [plans, formik.values.plan_id]
  );

  return {
    formik,
    plans,
    isEdit,
    planObj,
    pricing,
    isLocked,
    isOfferSaving,
    isPricingLoading,
    usedSeats: editOfferData?.used_seats ?? 0,
    statusLabel: editOfferData?.status_label,
    handleMobileTextChange,
    handleMobileCsvChange,
    handleSampleCsvDownload,
    getFieldError,
    closeModal,
  };
};

export default useAddCorporateOffer;
